import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import {
    DocumentTextIcon,
    ChartBarIcon,
    ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { event } from '../utils/analytics';
import CarouselReviews from '../components/CarouselReview';
import PublicCvPreview from '../components/PublicCvPreview';

const FAQ_ITEMS = [
    {
        q: 'Comment fonctionne CV Analyse ?',
        a: 'Vous importez un CV (PDF ou Word) sans compte : score et 3 conseils. Le rapport IA complet (détail, ATS, CV restructuré) s’ouvre dès 5 €.',
    },
    {
        q: 'CV Analyse est-il gratuit ?',
        a: 'Oui : score et 3 conseils sans inscription ni carte. Le rapport complet se débloque dès 5 €, sans abonnement.',
    },
    {
        q: 'Mes données sont-elles protégées ?',
        a: 'Oui. CV Analyse respecte le RGPD. Vos CV restent confidentiels. Vous pouvez supprimer votre compte et vos données à tout moment.',
    },
    {
        q: 'Combien de temps prend une analyse ?',
        a: 'L’aperçu s’affiche en quelques secondes. Le rapport IA complet, après paiement, prend moins de 2 minutes.',
    },
    {
        q: 'CV Analyse aide-t-il à passer les ATS ?',
        a: 'Oui. L’analyse regarde la structure, les mots-clés et le format, puis propose des pistes pour rester lisible par un ATS recruteur.',
    },
    {
        q: 'Puis-je analyser mon CV pour plusieurs secteurs ?',
        a: 'Oui. Chaque analyse est liée au secteur choisi (tech, finance, marketing, santé, éducation, etc.).',
    },
    {
        q: 'Quels formats de CV sont acceptés ?',
        a: 'PDF texte ou Word (.docx). Les PDF Canva, scannés ou en image ne peuvent souvent pas être lus : exportez un PDF avec du texte sélectionnable.'
    },
    {
        q: 'Comment améliorer mon score ?',
        a: 'Le score est la moyenne de 5 critères (structure, clarté, mots-clés, secteur, quantification). Corrigez d’abord le plus faible, puis relancez une analyse. Détail : cv-analyse.fr/methode.',
    },
];

const Home = () => {
    const user = useSelector((state) => state.auth.user);
    const token = useSelector((state) => state.auth.token);
    const navigate = useNavigate();

    useEffect(() => {
        if (token && user) {
            navigate('/upload');
        }
    }, [token, user, navigate]);

    return (
        <div className="min-h-screen bg-slate-100 pt-16">
            <Helmet>
                <title>CV Analyse — Analyse de CV par IA | Score et conseils en 2 minutes</title>
                <meta name="description" content="Analysez votre CV sans inscription : score et 3 conseils offerts. Rapport IA complet dès 5 €." />
                <meta property="og:title" content="CV Analyse — Analyse de CV par IA" />
                <meta property="og:description" content="Score et 3 conseils sans inscription. Rapport IA complet dès 5 €." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.cv-analyse.fr" />
                <meta property="og:image" content="https://www.cv-analyse.fr/og-image-1200x630.png" />
                <meta property="og:image:alt" content="CV Analyse - Analyse de CV par IA" />
                <meta property="og:site_name" content="CV Analyse" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="CV Analyse — Analyse de CV par IA" />
                <meta name="twitter:description" content="Score et 3 conseils sans inscription. Rapport IA complet dès 5 €." />
                <meta name="twitter:image" content="https://www.cv-analyse.fr/og-image-1200x630.png" />
                <link rel="canonical" href="https://www.cv-analyse.fr/" />
                <script type="application/ld+json">{JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'FAQPage',
                    mainEntity: FAQ_ITEMS.map((item) => ({
                        '@type': 'Question',
                        name: item.q,
                        acceptedAnswer: { '@type': 'Answer', text: item.a },
                    })),
                })}</script>
            </Helmet>

            <section className="px-4 sm:px-6 pt-14 sm:pt-20 pb-16 mx-auto max-w-6xl">
                <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div className="max-w-xl">
                        <p className="inline-flex items-center rounded-full bg-blue-50 text-blue-800 text-sm font-medium px-3 py-1 mb-5">
                            Sans inscription · score en quelques secondes
                        </p>
                        <h1 className="font-display text-[2.35rem] sm:text-5xl lg:text-[3.4rem] font-medium text-slate-900 tracking-[-0.03em] leading-[1.08] break-words">
                            Votre CV, évalué avec{' '}
                            <span className="italic text-blue-800">l’exigence d’un recruteur</span>
                        </h1>
                        <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                            <strong className="font-semibold text-slate-900">Score et 3 conseils</strong> offerts.
                            Le rapport IA complet dès 5 €.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3">
                            <a
                                href="#apercu"
                                className="btn-primary py-3 px-6 text-base"
                                onClick={() => event({ action: 'hero_cta_click', category: 'engagement', label: 'lancer_analyse' })}
                            >
                                Voir mon score
                            </a>
                            <Link to="/demo" className="text-sm font-medium text-slate-700 underline underline-offset-4 decoration-slate-300 hover:text-blue-700 hover:decoration-blue-700">
                                Consulter un exemple
                            </Link>
                        </div>
                        <ul className="mt-7 flex flex-wrap gap-2 text-sm text-slate-600">
                            <li className="rounded-full bg-white border border-slate-200 px-3 py-1">PDF ou Word</li>
                            <li className="rounded-full bg-white border border-slate-200 px-3 py-1">Sans compte</li>
                            <li className="rounded-full bg-white border border-slate-200 px-3 py-1">1 aperçu par jour</li>
                        </ul>
                    </div>
                    <PublicCvPreview />
                </div>
            </section>

            <section className="px-4 sm:px-6 pb-20 mx-auto max-w-6xl">
                <h2 className="text-2xl font-semibold text-slate-900 tracking-tight mb-8">
                    Ce que vous obtenez
                </h2>
                <div className="card-surface grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-200">
                    <div className="p-6 sm:p-7">
                        <ChartBarIcon className="w-5 h-5 text-blue-600 mb-4" />
                        <h3 className="text-base font-semibold text-slate-900 mb-2">Analyse détaillée</h3>
                        <p className="text-slate-600 text-sm leading-relaxed">
                            Un score sur 100 et des recommandations par section, adaptées à votre secteur.
                        </p>
                    </div>
                    <div className="p-6 sm:p-7">
                        <ShieldCheckIcon className="w-5 h-5 text-blue-600 mb-4" />
                        <h3 className="text-base font-semibold text-slate-900 mb-2">Données protégées</h3>
                        <p className="text-slate-600 text-sm leading-relaxed">
                            RGPD : vos CV restent confidentiels. Vous pouvez supprimer votre compte à tout moment.
                        </p>
                    </div>
                    <div className="p-6 sm:p-7">
                        <DocumentTextIcon className="w-5 h-5 text-blue-600 mb-4" />
                        <h3 className="text-base font-semibold text-slate-900 mb-2">Résultat en 2 minutes</h3>
                        <p className="text-slate-600 text-sm leading-relaxed">
                            Importez un PDF ou un Word, recevez le score et les conseils sans attendre.
                        </p>
                    </div>
                </div>
            </section>

            <section className="px-4 sm:px-6 pb-20 overflow-hidden">
                <div className="max-w-6xl mx-auto">
                    <CarouselReviews />
                </div>
            </section>

            <section className="px-4 sm:px-6 pb-20 mx-auto max-w-6xl">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
                    <h2 className="text-2xl font-semibold text-slate-900 tracking-tight">
                        Ressources
                    </h2>
                    <Link to="/guides" className="text-sm font-medium text-blue-700 hover:text-blue-800">
                        Tous les guides
                    </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200">
                    <Link
                        to="/guide/analyse-cv-ia"
                        className="bg-white p-6 hover:bg-slate-50"
                        onClick={() => event({ action: 'resource_click', category: 'engagement', label: '/guide/analyse-cv-ia' })}
                    >
                        <h3 className="text-base font-semibold text-slate-900 mb-2">Analyse de CV par IA</h3>
                        <p className="text-slate-600 text-sm leading-relaxed">Critères évalués, erreurs à éviter, check-list.</p>
                    </Link>
                    <Link
                        to="/guide/optimiser-cv-ats"
                        className="bg-white p-6 hover:bg-slate-50"
                        onClick={() => event({ action: 'resource_click', category: 'engagement', label: '/guide/optimiser-cv-ats' })}
                    >
                        <h3 className="text-base font-semibold text-slate-900 mb-2">Optimiser son CV pour les ATS</h3>
                        <p className="text-slate-600 text-sm leading-relaxed">Structure, mots-clés et format compatible recruteur.</p>
                    </Link>
                </div>
            </section>

            <section className="px-4 sm:px-6 pb-20 mx-auto max-w-6xl" aria-labelledby="faq-heading">
                <div className="grid lg:grid-cols-[minmax(0,18rem)_1fr] gap-10 lg:gap-16">
                    <div>
                        <h2 id="faq-heading" className="text-2xl font-semibold text-slate-900 tracking-tight">
                            Questions fréquentes
                        </h2>
                        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                            Les réponses utiles avant de lancer une analyse.
                        </p>
                    </div>
                    <div className="border-t border-slate-200">
                        {FAQ_ITEMS.map((item, index) => (
                            <details key={item.q} className="group border-b border-slate-200" open={index === 0}>
                                <summary className="flex items-start justify-between gap-4 py-5 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                                    <h3 className="text-base font-semibold text-slate-900">{item.q}</h3>
                                    <span className="mt-0.5 text-slate-400 text-lg leading-none group-open:rotate-45 transition-transform" aria-hidden="true">+</span>
                                </summary>
                                <p className="pb-5 pr-8 text-slate-600 text-sm leading-relaxed">{item.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            <section className="px-4 sm:px-6 pb-20 mx-auto max-w-6xl">
                <div className="cta-band flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 text-left">
                    <div>
                        <p className="text-lg font-semibold">Prêt à analyser votre CV ?</p>
                        <p className="text-slate-300 text-sm mt-1">Score et 3 conseils offerts — rapport complet dès 5 €.</p>
                    </div>
                    <a href="#apercu" className="btn-primary shrink-0">
                        Voir mon score
                    </a>
                </div>
            </section>
        </div>
    );
};

export default Home;
